import { useEffect, useRef, useState } from 'react';

/**
 * Counts a duration the server issued down locally.
 *
 * Anchored to the moment the value arrived rather than to the deadline it came
 * with: the deadline is on the server's clock, and a phone whose clock is two
 * minutes out would otherwise show a countdown that is two minutes wrong. Each
 * new value from the server re-anchors it, so the two can never drift far.
 *
 * `null` means there is nothing to count; the hook then does no work at all.
 */
export function useCountdown(remainingMs: number | null): number {
  const [elapsed, setElapsed] = useState(0);
  const anchoredAt = useRef(0);

  useEffect(() => {
    anchoredAt.current = Date.now();
    setElapsed(0);
    // Nothing to count, or nothing left of it: a ticker whose value is already
    // clamped to zero would just be a re-render every quarter second.
    if (remainingMs === null || remainingMs <= 0) return;

    // Four times a second, not once: on a one-second tick the displayed
    // number is up to a second stale, which on a countdown is the difference
    // between "0:01" and something having already happened.
    const id = window.setInterval(() => setElapsed(Date.now() - anchoredAt.current), 250);
    return () => window.clearInterval(id);
  }, [remainingMs]);

  if (remainingMs === null) return 0;
  return Math.max(0, remainingMs - elapsed);
}
